import type { StorefrontThemePack } from './types'
import { cn } from '@/lib/utils'

export type ProductCardVariant = 'default' | 'minimal' | 'luxury' | 'bold'
export type RefButtonPreset = 'primary' | 'secondary' | 'ghost' | 'pill' | 'sharp'
export type HeadingStylePreset = 'clean' | 'editorial' | 'bold'
export type SectionSpacingPreset = 'tight' | 'normal' | 'loose'
export type ThemeUiMode = 'light' | 'dark'
export type ThemeUiRadius = 'none' | 'md' | 'xl'
export type ThemeUiFont = 'sans' | 'serif'
export type ThemeUiDensity = 'tight' | 'comfortable'
export type SectionSurfacePattern = 'uniform' | 'alternate'

/** Scroll + ambient motion intensity. Default `subtle` preserves legacy storefront motion. */
export type MotionLevel = 'none' | 'subtle' | 'premium'

export type LayoutThemeUiInput = {
  mode?: ThemeUiMode
  radius?: ThemeUiRadius
  /** Section rhythm between blocks: compact → tight, normal, airy → loose */
  spacing?: 'compact' | 'normal' | 'airy'
  font?: ThemeUiFont
  density?: ThemeUiDensity
  productCard?: ProductCardVariant
  buttons?: RefButtonPreset
  headingStyle?: HeadingStylePreset
  /** Alternate subtle background per block row (`.sf-block-outer`). */
  sectionSurfaces?: SectionSurfacePattern
  /** Show quick-add on product cards when parent does not override `enableQuickAdd`. */
  productCardQuickAdd?: boolean
  /** Fade-up blocks on scroll; set false to disable globally. */
  blockScrollMotion?: boolean
  /** Ambient motion tier; `none` disables scroll reveal + decorative CSS motion. */
  motionLevel?: MotionLevel
}

export type StorefrontUiTokens = {
  mode: ThemeUiMode
  radius: ThemeUiRadius
  sectionSpacing: SectionSpacingPreset
  font: ThemeUiFont
  density: ThemeUiDensity
  productCard: ProductCardVariant
  buttonPreset: RefButtonPreset
  headingStyle: HeadingStylePreset
  sectionSurfacePattern: SectionSurfacePattern
  productCardQuickAdd: boolean
  blockScrollMotion: boolean
  motionLevel: MotionLevel
}

export const DEFAULT_UI: StorefrontUiTokens = {
  mode: 'light',
  radius: 'md',
  sectionSpacing: 'normal',
  font: 'sans',
  density: 'comfortable',
  productCard: 'default',
  buttonPreset: 'primary',
  headingStyle: 'clean',
  sectionSurfacePattern: 'uniform',
  productCardQuickAdd: false,
  blockScrollMotion: true,
  motionLevel: 'subtle',
}

function parseMotionLevel(v: unknown): MotionLevel {
  if (v === 'none' || v === 'premium') return v
  return 'subtle'
}

function mapSpacing(s: unknown): SectionSpacingPreset {
  if (s === 'compact' || s === 'tight') return 'tight'
  if (s === 'airy' || s === 'loose') return 'loose'
  if (s === 'normal') return 'normal'
  return 'normal'
}

export function parseStorefrontUi(
  raw: Record<string, unknown>,
  opts?: { legacyLayoutDensity?: 'relaxed' | 'compact' }
): StorefrontUiTokens {
  const ui = raw.ui && typeof raw.ui === 'object' ? (raw.ui as Record<string, unknown>) : {}
  const motionLevel = parseMotionLevel(ui.motionLevel)
  const legacyTight = opts?.legacyLayoutDensity === 'compact'
  const density: ThemeUiDensity =
    ui.density === 'tight'
      ? 'tight'
      : ui.density === 'comfortable'
        ? 'comfortable'
        : legacyTight
          ? 'tight'
          : 'comfortable'
  return {
    ...DEFAULT_UI,
    mode: ui.mode === 'dark' ? 'dark' : 'light',
    radius: ui.radius === 'none' || ui.radius === 'xl' ? ui.radius : 'md',
    sectionSpacing: mapSpacing(ui.spacing),
    font: ui.font === 'serif' ? 'serif' : 'sans',
    density,
    productCard:
      ui.productCard === 'minimal' || ui.productCard === 'luxury' || ui.productCard === 'bold'
        ? ui.productCard
        : 'default',
    buttonPreset:
      ui.buttons === 'secondary' ||
      ui.buttons === 'ghost' ||
      ui.buttons === 'pill' ||
      ui.buttons === 'sharp'
        ? ui.buttons
        : 'primary',
    headingStyle:
      ui.headingStyle === 'editorial' || ui.headingStyle === 'bold' ? ui.headingStyle : 'clean',
    sectionSurfacePattern: ui.sectionSurfaces === 'alternate' ? 'alternate' : 'uniform',
    productCardQuickAdd: ui.productCardQuickAdd === true,
    motionLevel,
    blockScrollMotion:
      motionLevel === 'none' ? false : ui.blockScrollMotion === false ? false : true,
  }
}

export function uiRootClasses(ui: StorefrontUiTokens): string {
  return [
    `sf-ui-mode-${ui.mode}`,
    `sf-ui-radius-${ui.radius}`,
    `sf-ui-section-${ui.sectionSpacing}`,
    `sf-ui-font-${ui.font}`,
    `sf-ui-density-${ui.density}`,
    `sf-ui-pc-${ui.productCard}`,
    `sf-ui-btn-${ui.buttonPreset}`,
    `sf-ui-heading-${ui.headingStyle}`,
    ui.sectionSurfacePattern === 'alternate' ? 'sf-section-pattern-alternate' : '',
    `sf-motion-level-${ui.motionLevel}`,
  ]
    .filter(Boolean)
    .join(' ')
}

function motionTimingCssVars(level: MotionLevel): Record<string, string> {
  const bundle = {
    '--sf-duration-fast': '180ms',
    '--sf-duration-base': '280ms',
    '--sf-duration-slow': '420ms',
    '--sf-duration-reveal': '650ms',
  } as const
  if (level === 'none') {
    return {
      ...bundle,
      '--sf-duration': '180ms',
      '--sf-reveal-translate-y': '0px',
      '--sf-reveal-scale-from': '1',
      '--sf-reveal-duration': '0.001ms',
    }
  }
  if (level === 'premium') {
    return {
      ...bundle,
      '--sf-duration': '240ms',
      '--sf-reveal-translate-y': '48px',
      '--sf-reveal-scale-from': '0.98',
      '--sf-reveal-duration': '700ms',
    }
  }
  return {
    ...bundle,
    '--sf-duration': '220ms',
    '--sf-reveal-translate-y': '22px',
    '--sf-reveal-scale-from': '1',
    '--sf-reveal-duration': '650ms',
  }
}

export function uiCssVars(ui: StorefrontUiTokens): Record<string, string> {
  const out: Record<string, string> = { ...motionTimingCssVars(ui.motionLevel) }
  if (ui.font === 'serif') {
    out['--ref-font'] = "'Lora', Georgia, 'Times New Roman', serif"
  }
  if (ui.radius === 'none') {
    out['--ref-radius-sm'] = '0'
    out['--ref-radius-md'] = '0'
    out['--ref-radius-lg'] = '0'
    out['--ref-radius-xl'] = '0'
  } else if (ui.radius === 'xl') {
    out['--ref-radius-sm'] = '0.75rem'
    out['--ref-radius-md'] = '1rem'
    out['--ref-radius-lg'] = '1.25rem'
    out['--ref-radius-xl'] = '1.75rem'
  }
  if (ui.mode === 'dark') {
    out['--ref-bg'] = '#0c0a09'
    out['--ref-bg-elevated'] = '#1c1917'
    out['--ref-text'] = '#fafaf9'
    out['--ref-text-secondary'] = '#d6d3d1'
    out['--ref-muted'] = '#a8a29e'
    out['--ref-border'] = '#292524'
    out['--ref-border-strong'] = '#44403c'
  }
  return out
}

export function sectionSiblingClass(ui: StorefrontUiTokens, blockOverride?: unknown): string {
  const sp =
    typeof blockOverride === 'string' ? mapSpacing(blockOverride) : ui.sectionSpacing
  if (sp === 'tight') return 'sf-block-gap-tight'
  if (sp === 'loose') return 'sf-block-gap-loose'
  return 'sf-block-gap-normal'
}

export function storefrontSectionTitleClass(ui: StorefrontUiTokens): string {
  const base = 'text-2xl font-bold tracking-tight text-ref-text sm:text-3xl'
  if (ui.headingStyle === 'editorial') {
    return `${base} sf-heading-title--editorial`
  }
  if (ui.headingStyle === 'bold') {
    return `${base} sf-heading-title--bold`
  }
  return `${base} sf-heading-title--clean`
}

export function refCtaButtonClass(
  theme: { ui: StorefrontUiTokens; components: StorefrontThemePack['components'] },
  role: 'solid' | 'outline' = 'solid'
): string {
  const preset = theme.ui.buttonPreset
  const base = role === 'outline' ? 'ref-btn ref-btn-outline' : 'ref-btn ref-btn-primary'
  const packBtn = theme.components.button
  return [base, packBtn, `sf-ref-btn-role-${role}`, `sf-ref-btn-${preset}`].filter(Boolean).join(' ')
}

/** Shadcn `Button` / `buttonVariants` CTAs (hero, legacy rows) — scoped under `.ref-storefront`. */
export function storefrontShadcnCtaChrome(theme: { ui: StorefrontUiTokens }): string {
  return `sf-shadcn-cta sf-shadcn-cta--${theme.ui.buttonPreset}`
}

export function heroDisplayHeadingClass(
  ui: StorefrontUiTokens,
  opts: { mobile: boolean; productSlide?: boolean }
): string {
  const { mobile, productSlide } = opts
  const sizeBanner = mobile ? 'text-3xl' : 'text-5xl md:text-7xl lg:text-8xl'
  const sizeProduct = mobile ? 'text-2xl' : 'text-4xl md:text-6xl lg:text-7xl'
  const base = cn(
    'font-black max-w-3xl leading-tight mb-6 animate-fade-in-up text-white',
    productSlide ? sizeProduct : sizeBanner
  )
  if (ui.headingStyle === 'editorial') {
    return cn(base, 'font-serif font-semibold tracking-tight italic')
  }
  if (ui.headingStyle === 'bold') {
    return cn(base, 'uppercase tracking-wide')
  }
  return cn(base, 'tracking-tight')
}
